import React, { useState, useEffect } from 'react';
import { Calendar, Mail, Truck, Send, X } from 'lucide-react';
import { DynamicHeight } from '../components/hooks/PageHeightCalculator';
import SuccessPopup from '../components/popups/SuccessPopup'
import { API_BASE_URL } from '../config'

const LetterComposer = () => {
  const [profile, setProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [mailType, setMailType] = useState('email');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState('3');
  const [customDate, setCustomDate] = useState('');
  const [isPhysical, setIsPhysical] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [emailReminder, setEmailReminder] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [letterContent, setLetterContent] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    name_on_address: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });



// Validation function for dates
const isValidDate = (dateString) => {
  if (!dateString) return false;
  
  const selectedDate = new Date(dateString);
  // Check if date is valid
  if (isNaN(selectedDate.getTime())) return false;
  
  const minDate = new Date();
  minDate.setMonth(minDate.getMonth() + 3);
  // Reset time components for consistent comparison
  minDate.setHours(0, 0, 0, 0);
  selectedDate.setHours(0, 0, 0, 0);
  
  return selectedDate >= minDate;
};

// Validation function for letter content
const hasValidContent = () => {
  if (!letterContent) return false;
  const trimmedContent = letterContent.trim();
  return trimmedContent.length > 0 && trimmedContent.length <= 50000; // Example max length
};

// Validation function for delivery time
const hasValidDeliveryTime = () => {
  if (deliveryTime === 'custom') {
    return isValidDate(customDate);
  }
  return ['3', '6', '12'].includes(deliveryTime);
};

// Validation function for email delivery
const isEmailDeliveryValid = () => {
  return hasValidContent() && hasValidDeliveryTime();
};

// Validation function for physical delivery
const isPhysicalDeliveryValid = () => {
  if (!hasValidContent() || !hasValidDeliveryTime()) {
    return false;
  }

  // Validate address fields
  return Object.entries(formData).every(([key, value]) => {
    if (value === null || value === undefined) return false;
    const trimmedValue = String(value).trim();
    
    // Additional validation for specific fields
    switch (key) {
      case 'zipCode':
        return /^\d{5}(-\d{4})?$/.test(trimmedValue); // US ZIP code format
      case 'name_on_address':
        // Require at least two words for full name
        return trimmedValue.split(/\s+/).filter(part => part.length > 0).length >= 2;
      default:
        return trimmedValue.length > 0;
    }
  });
};

// Combined validation check
const isFormValid = () => {
  return isPhysical ? isPhysicalDeliveryValid() : isEmailDeliveryValid();
};

  // Load draft when component mounts
  useEffect(() => {
    loadDraft();
  }, []);

  // Autosave functionality with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (letterContent) {
        saveDraft();
      }
    }, 1000); // Save after 1 second of no typing

    return () => clearTimeout(timeoutId);
  }, [letterContent]);

  const loadDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/drafts/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLetterContent(data.content || '');
        setDeliveryTime(data.delivery_time || '3');
        setCustomDate(data.custom_date || '');
        setIsPhysical(data.is_physical || false);
        setFormData(data.address_data || formData);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  };

  const deleteDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/drafts/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setLetterContent('');
        setDeliveryTime('3');
        setCustomDate('');
        setIsPhysical(false);
        setFormData({
          name_on_address: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        });
        setSaveStatus('Draft deleted');
        setTimeout(() => setSaveStatus(''), 2000);
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      setSaveStatus('Error deleting draft');
    }
  };

  const saveDraft = async () => {
    setSaveStatus('Saving...');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/drafts/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: letterContent,
          delivery_time: deliveryTime,
          custom_date: customDate,
          is_physical: isPhysical,
          address_data: formData
        }),
      });

      if (response.ok) {
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        setSaveStatus('Error saving');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setSaveStatus('Error saving');
    }
  };

  const getMinDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3);
    return date.toISOString().split('T')[0];
  };

  const handleDeliveryChange = (value) => {
    setDeliveryTime(value);
    if (value !== 'custom') {
      setCustomDate('');
    }
  };

// Then update the handlePhysicalToggle function
const handlePhysicalToggle = () => {
  const newIsPhysical = !isPhysical;
  setIsPhysical(newIsPhysical);
  setShowAddress(newIsPhysical);
  setMailType(newIsPhysical ? 'physical' : 'email');
};

useEffect(() => {
  const fetchProfileData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoadingProfile(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      setProfile(data.user);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  fetchProfileData();
}, []); // Run once on component mount

// Update your sendEmail function
const sendEmail = async () => {
  if (!isFormValid() || isSending) return;
  
  try {
    setIsSending(true);
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Please log in to send emails');
    }

    if (!profile) {
      throw new Error('Please wait while we load your profile');
    }

    const emailData = {
      mail_type: 'email',
      recipient_email: profile.email,
      recipient_name: `${profile.name} ${profile.surname}`,
      subject: 'Future Letter',
      email_content: letterContent,
      mail_status: 'frozen',
      send_reminder: emailReminder ? 1 : 0,
      scheduled_send_time: deliveryTime === 'custom' 
        ? new Date(customDate).toISOString()
        : new Date(Date.now() + (parseInt(deliveryTime) * 30 * 24 * 60 * 60 * 1000)).toISOString()
    };

    const response = await fetch(`${API_BASE_URL}/api/letters/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(emailData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to schedule email');
    }

    const result = await response.json();
    await deleteDraft();
    return result;
    
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  } finally {
    setIsSending(false);
  }
};

  const sendLetter = async () => {
    if (!isFormValid() || isSending) return;
    
    try {
      setIsSending(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Please log in to send letters');
      }

      // Ensure we have a valid name with at least first and last name
      const nameParts = formData.name_on_address.trim().split(/\s+/);
      if (nameParts.length < 2) {
        throw new Error('Please provide both first and last name');
      }
      
      const [firstName, ...lastNameParts] = nameParts;
      const lastName = lastNameParts.join(' ');
      
      const letterData = {
        mail_type: 'physical',
        delivery_name: firstName,
        delivery_surname: lastName,
        delivery_street: formData.address,
        delivery_town: formData.city,
        delivery_zipcode: formData.zipCode,
        delivery_region: formData.state,
        delivery_state: formData.state,
        letter_content: letterContent,
        page_count: Math.ceil(letterContent.length / 3000),
        paper_type: "standard",
        envelope_type: "standard",
        double_sided: 0,
        color_print: 0,
        postal_class: "first",
        mail_status: "frozen",
        postage_cost: 1,
        send_reminder: emailReminder ? 1 : 0,
        scheduled_send_time: deliveryTime === 'custom' 
          ? new Date(customDate).toISOString()
          : new Date(Date.now() + (parseInt(deliveryTime) * 30 * 24 * 60 * 60 * 1000)).toISOString()
      };

      const response = await fetch(`${API_BASE_URL}/api/letters/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(letterData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to schedule letter');
      }

      const result = await response.json();
      
      // Delete the draft first to ensure we don't have lingering content
      await deleteDraft();
      
      // Show success message before closing dialog
      // alert('Letter scheduled successfully! Check the dashboard for delivery status.');

      return result; // Return the result for the handler to use
      
    } catch (error) {
      console.error('Error sending letter:', error);
      // alert(error instanceof Error ? error.message : 'Failed to schedule letter');
    } finally {
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setLetterContent('');
    setDeliveryTime('3');
    setCustomDate('');
    setIsPhysical(false);
    setShowAddress(false);
    setFormData({
      name_on_address: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    });
  };

  const handleSendConfirm = async () => {
    if (!isFormValid()) return;
    
    try {
      const result = mailType === 'email' 
        ? await sendEmail()
        : await sendLetter();
        
      if (result) {
        setShowConfirmDialog(false);
        setShowSuccessPopup(true);
        resetForm();
      }
    } catch (error) {
      console.error(`Failed to send ${mailType}:`, error);
      alert(error instanceof Error ? error.message : `Failed to schedule ${mailType}`);
    }
  };

  return (
    <DynamicHeight className="flex flex-col">
      <div className="flex-1 flex flex-col relative">
        <textarea
          className="flex-1 w-full p-6 resize-none focus:ring-2 focus:ring-primary focus:outline-none border-2 border-gray-200"
          placeholder="Write your letter here..."
          value={letterContent}
          onChange={(e) => setLetterContent(e.target.value)}
        />
        {saveStatus && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-gray-800 text-white text-sm rounded-md opacity-75">
            {saveStatus}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center space-x-4">
              <Calendar className="h-5 w-5 text-gray-500" />
              <div className="flex space-x-2">
                {['3', '6', '12', 'custom'].map((time) => (
                  <button
                    key={time}
                    onClick={() => handleDeliveryChange(time)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                      ${deliveryTime === time 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {time === 'custom' ? 'Custom' : `${time}m`}
                  </button>
                ))}
              </div>
              {deliveryTime === 'custom' && (
                <input
                  type="date"
                  min={getMinDate()}
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 border-0 focus:ring-2 focus:ring-indigo-500"
                />
              )}
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-6 border-r border-gray-200 pr-6">
                <div className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-gray-500" />
                  <span className="text-sm text-gray-700">Email</span>
                </div>
                <button
                  onClick={handlePhysicalToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${isPhysical ? 'bg-primary' : 'bg-gray-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${isPhysical ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
                <div className="flex items-center space-x-2">
                  <Truck className="h-5 w-5 text-gray-500" />
                  <span className="text-sm text-gray-700">Physical</span>
                </div>
              </div>

              <button
                onClick={() => isFormValid() && setShowConfirmDialog(true)}
                className={`flex items-center space-x-2 px-6 py-2 rounded-md transition-colors
                  ${isFormValid() 
                    ? 'bg-primary hover:bg-accent text-white' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                disabled={!isFormValid()}
              >
                <Send className="h-4 w-4" />
                <span>Send Letter</span>
              </button>
            </div>
          </div>

          {showAddress && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex justify-between items-center">
                <p className="text-sm text-blue-700">
                  Sending a physical mail will use 1 sending credit from your balance
                </p>
                <Mail className="h-5 w-5 text-blue-500" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(formData).map(([key, value]) => (
                  <input
                    key={key}
                    type="text"
                    placeholder={key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    value={value}
                    onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                ))}
              </div>

              <div className="flex items-center justify-end space-x-4 mt-4">
                <span className="text-sm text-gray-700">
                  Send me an email one month before delivery to confirm my address
                </span>
                <button
                  onClick={() => setEmailReminder(!emailReminder)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0
                    ${emailReminder ? 'bg-primary' : 'bg-gray-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${emailReminder ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showConfirmDialog && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 transform transition-all">
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900">
            Are you finished writing?
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Once confirmed, your message will be scheduled for delivery
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
          <button 
            onClick={() => setShowConfirmDialog(false)}
            className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light rounded-lg border border-gray-300 transition-colors"
          >
            No, keep writing
          </button>
          
          <button
            onClick={handleSendConfirm}
            disabled={isSending}
            className={`inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors
              ${isSending 
                ? 'bg-primary/70 cursor-not-allowed' 
                : 'bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
              }`}
          >
            {isSending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Scheduling...
              </>
            ) : (
              'Yes, schedule delivery'
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      {showSuccessPopup && (
        <SuccessPopup 
          onClose={() => setShowSuccessPopup(false)}
          redirectTimeout={3}
          isPhysical={isPhysical}
        />
      )}
    </DynamicHeight>
  );
};

export default LetterComposer;